import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import DataTable from '../../components/Shared/DataTable';
import { schoolAdminNavItems } from '../../constants/navigation';
import EmptyState from '../../components/Shared/EmptyState';

const TeachersPage = () => {
  const { currentUser } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [teacherForm, setTeacherForm] = useState({ fullName: '', subject: '', gradeLevel: '' });
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState(null);

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiRequest(
        `/api/school-admin/teachers?page=${currentPage}&per_page=20&search=${searchQuery}&status=${statusFilter}`,
        'GET'
      );
      setTeachers(data.teachers || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const handleCreateCode = async () => {
    if (!teacherForm.fullName || !teacherForm.gradeLevel) {
      alert('Please fill in name and grade level');
      return;
    }
    setCreateLoading(true);
    try {
      const response = await apiRequest('/api/school-admin/codes/teacher/create', 'POST', {
        full_name: teacherForm.fullName,
        subject: teacherForm.subject,
        grade_level: teacherForm.gradeLevel,
      });
      setCreatedCode(response);
      fetchTeachers();
    } catch (err) {
      alert(`Failed to create code: ${err.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    const lines = bulkText.trim().split('\n').filter(l => l.trim());
    if (lines.length === 0) {
      alert('Enter at least one teacher');
      return;
    }
    const teachersList = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      return {
        full_name: parts[0] || '',
        grade_level: parts[1] || 'elementary',
        subject: parts[2] || '',
      };
    });
    setCreateLoading(true);
    try {
      const response = await apiRequest('/api/school-admin/codes/teacher/create-bulk', 'POST', {
        teachers: teachersList,
      });
      setBulkResult(response);
      fetchTeachers();
    } catch (err) {
      alert(`Bulk create failed: ${err.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeactivate = async (teacherId, teacherName) => {
    if (!window.confirm(`Deactivate ${teacherName}? Their students will remain but this teacher will no longer be able to log in.`)) {
      return;
    }
    try {
      await apiRequest(`/api/school-admin/teachers/${teacherId}/deactivate`, 'PUT');
      fetchTeachers();
    } catch (err) {
      alert(`Failed to deactivate: ${err.message}`);
    }
  };

  const teacherColumns = [
    {
      key: 'full_name',
      label: 'Teacher',
      sortable: true,
      render: (t) => (
        <div>
          <p className="font-medium text-gray-900">{t.full_name}</p>
          <p className="text-xs text-gray-500">{t.email}</p>
        </div>
      ),
    },
    { key: 'subject', label: 'Subject', sortable: true },
    {
      key: 'grade_level',
      label: 'Grade',
      sortable: true,
      render: (t) => <span className="capitalize">{t.grade_level || 'N/A'}</span>,
    },
    {
      key: 'student_count',
      label: 'Students',
      sortable: true,
      render: (t) => <span className="font-medium">{t.student_count || 0}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (t) => {
        if (!t.is_active) return <span className="text-gray-500">Inactive</span>;
        if (t.activation_code_used) return <span className="text-green-600 font-medium">Active</span>;
        return <span className="text-yellow-600">Pending</span>;
      },
    },
    {
      key: 'activation_code',
      label: 'Code',
      render: (t) => {
        if (t.activation_code_used) return <span className="text-green-600 text-xs">Activated</span>;
        if (!t.activation_code) return null;
        return (
          <button
            onClick={() => {
              navigator.clipboard.writeText(t.activation_code);
              alert(`Code copied: ${t.activation_code}`);
            }}
            className="text-indigo-600 hover:text-indigo-800 font-medium text-xs"
          >
            Copy Code
          </button>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (t) => (
        <div className="flex gap-2">
          {t.is_active !== false && t.activation_code_used && (
            <button
              onClick={() => handleDeactivate(t.id, t.full_name)}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Deactivate
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loading && teachers.length === 0) {
    return (
      <DashboardLayout title="Teachers" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading teachers...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Teachers" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Teacher Management</h2>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowCreateModal(true); setCreatedCode(null); setTeacherForm({ fullName: '', subject: '', gradeLevel: '' }); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
          >
            + Create Code
          </button>
          <button
            onClick={() => { setShowBulkModal(true); setBulkResult(null); setBulkText(''); }}
            className="px-4 py-2 bg-white text-indigo-600 border-2 border-indigo-600 rounded-lg hover:bg-indigo-50 font-medium text-sm"
          >
            Bulk Create
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
          Error: {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search teachers..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
        />
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { label: 'All', value: '' },
            { label: 'Active', value: 'active' },
            { label: 'Pending', value: 'pending' },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatusFilter(opt.value); setCurrentPage(1); }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                statusFilter === opt.value
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-gray-500">{pagination?.total || 0} total</span>
      </div>

      {!loading && teachers.length === 0 && !searchQuery && !statusFilter ? (
        <EmptyState
          icon="&#128104;&#8205;&#127979;"
          title="No Teachers Yet"
          description="Generate teacher codes to invite teachers to your school."
          actionLabel="+ Create Teacher Code"
          onAction={() => { setShowCreateModal(true); setCreatedCode(null); setTeacherForm({ fullName: '', subject: '', gradeLevel: '' }); }}
        />
      ) : (
        <DataTable
          columns={teacherColumns}
          data={teachers}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          emptyMessage="No teachers found matching your filters."
        />
      )}

      {/* Create Single Code Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Create Teacher Code</h3>
            {!createdCode ? (
              <>
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teacher Name*</label>
                    <input type="text" placeholder="Mrs. Jane Doe" value={teacherForm.fullName}
                      onChange={(e) => setTeacherForm({ ...teacherForm, fullName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input type="text" placeholder="Mathematics" value={teacherForm.subject}
                      onChange={(e) => setTeacherForm({ ...teacherForm, subject: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level*</label>
                    <select value={teacherForm.gradeLevel}
                      onChange={(e) => setTeacherForm({ ...teacherForm, gradeLevel: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white">
                      <option value="">Select...</option>
                      <option value="elementary">Elementary (K-5)</option>
                      <option value="middle">Middle School (6-8)</option>
                    </select>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button onClick={handleCreateCode} disabled={createLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                    {createLoading ? 'Creating...' : 'Create Code'}
                  </button>
                  <button onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-green-600 font-semibold mb-3">Code created!</p>
                <div className="bg-gray-50 rounded p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-1">Activation Code:</p>
                  <p className="font-mono text-xl text-indigo-600 font-bold">{createdCode.activation_code}</p>
                </div>
                <div className="flex space-x-3">
                  <button onClick={() => { navigator.clipboard.writeText(createdCode.activation_code); alert('Copied!'); }}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Copy Code</button>
                  <button onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Bulk Create Teacher Codes</h3>
            {!bulkResult ? (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Enter one teacher per line: <code className="bg-gray-100 px-1 rounded">Name, grade_level, Subject</code>
                </p>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                  placeholder={"Jane Doe, elementary, Math\nJohn Smith, middle, Science"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono mb-4"
                />
                <div className="flex space-x-3">
                  <button onClick={handleBulkCreate} disabled={createLoading}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                    {createLoading ? 'Creating...' : 'Create All'}
                  </button>
                  <button onClick={() => setShowBulkModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-green-600 font-semibold mb-3">
                  Created {bulkResult.created_count} codes ({bulkResult.error_count} errors)
                </p>
                {bulkResult.created?.length > 0 && (
                  <div className="bg-gray-50 rounded p-3 mb-3 max-h-48 overflow-y-auto">
                    {bulkResult.created.map((c) => (
                      <div key={c.teacher_id} className="flex justify-between items-center py-1 text-sm">
                        <span>{c.full_name}</span>
                        <button onClick={() => { navigator.clipboard.writeText(c.activation_code); }}
                          className="text-indigo-600 font-mono text-xs hover:text-indigo-800">{c.activation_code}</button>
                      </div>
                    ))}
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div className="bg-red-50 rounded p-3 mb-3 text-sm text-red-700">
                    {bulkResult.errors.map((e, i) => (
                      <p key={i}>{e.full_name || `Row ${e.index}`}: {e.error}</p>
                    ))}
                  </div>
                )}
                <button onClick={() => setShowBulkModal(false)}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TeachersPage;
